import React from "react";
import { useRecoilValue } from 'recoil';
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Container, Typography, FormControl, TextField, Button, InputLabel, Select, MenuItem } from "@mui/material";
import Grid from '@mui/material/Grid2';
import SearchIcon from '@mui/icons-material/Search';
import AddCircleIcon from '@mui/icons-material/AddCircle';

import { isDebug } from "../../utils";
import { userInfo } from "../../auth/AuthProvider";
import { useBoardManageListQuery } from "../../hooks/useBoard/useBoardManageQuery";

import ContentLayout from "../../layouts/ContentLayout";
import { ControlWriteBoard, ControlDataGrid, Columns4Manage, ControlSnackbar, ControlCircularProgressBar, Dialog4Confirm, Dialog4Alert, BoardType } from "../../components/board";


export default function Manage4Board () {
    const navigate = useNavigate();
    const auth = useRecoilValue(userInfo);
    const { t } = useTranslation();
    const isLoadingRef = React.useRef(false);
    const refConfirm = React.useRef(null);
    const refAlert = React.useRef(null);
    const refSnackbar = React.useRef(null);
    const showTimerControler = React.useRef(null);

    const [ pageTitle, setPageTitle ] = React.useState(t('nav.manage4board'));
    const [paginationModel, setPaginationModel] = React.useState({
        page: 0,
        pageSize: 10,
    });
    const defaultQueryOptions = {
        BoardType: "",
        ManageId: 0,
        Page: paginationModel.page + 1,
        PageSize: paginationModel.pageSize,
        BoardName: "",
    };
    const [queryOptions, setQueryOptions] = React.useState(defaultQueryOptions);
    const [ dataSet, setDataSet ] = React.useState({
        records: [],
        rowLength: 0,
        editable: false,
        isLoading: false,
    });
    const [openEditDialog, setOpenEditDialog] = React.useState({
        isShow: false,
        manageId : 0,
    }); // 등록 창
    const [selectedElements, setSelectedElements] = React.useState(BoardType);

    const { isError, isLoading, isFetching, isSuccess, error, data: gridData, refetch  } = useBoardManageListQuery(queryOptions);


    const closeEditDialog = (boolValue) => {
        if(boolValue) {
            // 조회를 다시한다.
        }
        setOpenEditDialog({
            isShow: false,
            manageId : 0,
        });
    };

    const runSearch = (e) => {
        e.preventDefault();
        
        if (isLoadingRef.current)  return;
        isLoadingRef.current = true;
        showTimerControler.current.showTimer(2);
        refetch();
        isLoadingRef.current = false;
    };

    const callRegistration = (e) => {
        e.preventDefault();

        if((auth.userId === undefined || auth.userId === null || auth.userId === '') || auth?.userType !== process.env.REACT_APP_BOARD_MANAGER_TYPE) {
            refSnackbar.current.openMessage(`${t('error.notlogin')}`);
            setTimeout(() => {
                navigate(`/login`);
            }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
        }
        else {
            if (isLoadingRef.current)  return;
            isLoadingRef.current = true;

            setOpenEditDialog({
                isShow: true,
                manageId : 0,
            });

            isLoadingRef.current = false;
        }
    };

    const onDataViewClick = (params) => {
        if((auth.userId === undefined || auth.userId === null || auth.userId === '') || auth?.userType !== process.env.REACT_APP_BOARD_MANAGER_TYPE) {
            refSnackbar.current.openMessage(`${t('error.notlogin')}`);
            setTimeout(() => {
                navigate(`/login`);
            }, process.env.REACT_APP_REDIRECT_TIME_WAIT);
        }
        else {
            if (isLoadingRef.current)  return;
            isLoadingRef.current = true;

            setOpenEditDialog({
                isShow: true,
                manageId : params.row.ManageId,
            });

            isLoadingRef.current = false;
        }
    };

    React.useEffect(() => {
        try {
            if(gridData?.response_code === 201)
            {
                setDataSet({...dataSet, records: gridData.data.Results, rowLength: gridData.data.TotalCnt});
            }
            else if (gridData?.message !== null && gridData?.message?.length > 0)
            {
                refAlert?.current?.showMessageBox(`${t('error.title')}`, JSON.stringify(gridData.message));
            }
            else if(isSuccess === false && (error !== undefined || error !== null || JSON.stringify(error) !== null ))
            {
                if (isDebug()) console.log("QueryBind[BLF]", new Date().toISOString(), error);
            }
        }
        catch(err) {
            console.log("No Data[BLF]", new Date().toISOString(), err);
        }

        return () => {
            setDataSet({
                records: [],
                rowLength: 0,
                editable: false,
                isLoading: false,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError, isLoading, isFetching, isSuccess, error, gridData]);
        
    React.useEffect(() => { 
        if (isDebug()) console.log("## UseEffect [paginationModel] paginationModel: ", new Date().toISOString(), JSON.stringify(paginationModel));
        setQueryOptions({...queryOptions, Page: paginationModel.page + 1, PageSize: paginationModel.pageSize});
        if (isDebug()) console.log("## UseEffect [paginationModel] queryOptions: ", new Date().toISOString(), JSON.stringify(queryOptions));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel]);


    return (
        <ContentLayout pageTitle={pageTitle} pageDescription={null} >
        
            <div className="page-description">
                <h1>게시판 관리</h1>
                <p>운영중인 게시판은 FAQ, QnA, 자유게시판입니다.</p>
            </div>

            {/* 검색 영역 */}
            <Container className="page-content" sx={{ p: 3 }}>
                <Grid container spacing={1}>
                    <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ paddingRight: '10px'}} >
                        <FormControl sx={{ m: 0, p: 0, minWidth: 200, width: "100%" }} size="small">
                            <InputLabel id="board-type-select-small-label">{t('searchfield.boardtype')}</InputLabel>
                            <Select
                                labelId="board-type-select-small-label"
                                id="board-type-select-small"
                                value={queryOptions.BoardType  || ''}
                                label={t('searchfield.boardtype')}
                                onChange={(v) => setQueryOptions({...queryOptions, BoardType:v.target.value}) }
                                size="small"
                            >
                                <MenuItem value="">
                                    <em>{t('searchfield.searchall')}</em>
                                </MenuItem>
                                {selectedElements ? selectedElements?.map(item => {
                                    return <MenuItem key={item.value} value={item.value}>{item.name}</MenuItem>
                                }) : null
                                }
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ paddingRight: '10px'}} >
                        <TextField
                            fullWidth
                            label={t('searchfield.board')}
                            id="filled-hidden-label-small"
                            defaultValue={queryOptions.BoardName}
                            onChange={(v) => setQueryOptions({...queryOptions, BoardName:v.target.value}) }
                            size="small"
                            placeholder={t('searchfield.board_placeholder')}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 12, md: 4 }} sx={{ textAlign: 'right' }} >
                        <div>
                            <Button variant="outlined" size="medium" startIcon={<SearchIcon />} onClick={runSearch}>
                                {t('button.search')}
                            </Button>
                            {' '}
                            <Button variant="outlined" size="medium" startIcon={<AddCircleIcon />} onClick={callRegistration} disabled={!auth.userId}>
                                {t('button.registration')}
                            </Button>
                        </div>
                    </Grid>
                </Grid>
            </Container>

            {/* 데이타 영역 */}
            <Container sx={{ width: '100%', height: '100%', alignItems: 'center', p: 1 }}>
                <ControlDataGrid t={t}
                    dataType="manage"
                    manageId={queryOptions.ManageId}
                    boardColumns={Columns4Manage}
                    dataSet={dataSet}
                    paginationModel={paginationModel}
                    setPaginationModel={setPaginationModel}
                    isUseManager={true}
                    onCellClick={onDataViewClick}
                />
            </Container>

            {/* 알림 메시지 영역 */}
            <Dialog4Alert ref={refAlert} />
            <Dialog4Confirm ref={refConfirm} />
            <ControlSnackbar ref={refSnackbar} />
            <ControlCircularProgressBar ref={showTimerControler} initialTime={1} />
            <Typography>&nbsp;</Typography>

            {/* 게시글 등록 영역 */}
            <ControlWriteBoard id="writeBoard" open={openEditDialog.isShow} closeEditDialog={closeEditDialog} manageId={openEditDialog.manageId} refMessage={showTimerControler.current} />

        </ContentLayout>
    );
}